import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { cartService, CartItem } from '../services/cartService';
import { orderService } from '../services/orderService';

interface AddressForm {
  fullName: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  pincode: string;
}

const emptyAddress = (): AddressForm => ({
  fullName: '', phone: '', addressLine1: '', addressLine2: '',
  city: '', state: '', pincode: '',
});

const formatAddress = (a: AddressForm) =>
  `${a.fullName}, ${a.phone}, ${a.addressLine1}${a.addressLine2 ? ', ' + a.addressLine2 : ''}, ${a.city}, ${a.state} - ${a.pincode}`;

const Field = ({
  label, value, onChange, placeholder, required = true, type = 'text', half = false,
}: {
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; required?: boolean; type?: string; half?: boolean;
}) => (
  <div className={half ? 'w-full' : 'w-full'}>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#DAA520] focus:border-transparent text-sm"
    />
  </div>
);

const AddressSection = ({
  title, form, setForm,
}: { title: string; form: AddressForm; setForm: (f: AddressForm) => void }) => {
  const set = (key: keyof AddressForm) => (v: string) => setForm({ ...form, [key]: v });
  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-xl font-semibold mb-5">{title}</h2>
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Full Name" value={form.fullName} onChange={set('fullName')} placeholder="Recipient's full name" />
          <Field label="Phone Number" value={form.phone} onChange={set('phone')} placeholder="10-digit mobile number" type="tel" />
        </div>
        <Field label="Address Line 1" value={form.addressLine1} onChange={set('addressLine1')} placeholder="House / Flat no., Building, Street" />
        <Field label="Address Line 2" value={form.addressLine2} onChange={set('addressLine2')} placeholder="Area, Colony, Landmark (optional)" required={false} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="City" value={form.city} onChange={set('city')} placeholder="City" />
          <Field label="State" value={form.state} onChange={set('state')} placeholder="State" />
          <Field label="Pincode" value={form.pincode} onChange={set('pincode')} placeholder="6-digit pincode" />
        </div>
      </div>
    </div>
  );
};

const CheckoutPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [useSameAddress, setUseSameAddress] = useState(true);
  const [notes, setNotes] = useState('');
  const [shipping, setShipping] = useState<AddressForm>(emptyAddress());
  const [billing, setBilling] = useState<AddressForm>(emptyAddress());

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    setCartItems(cartService.getCartItems(user.id));
    setLoading(false);
    // Pre-fill name from user
    setShipping(prev => ({ ...prev, fullName: user.name || '' }));
  }, [user, navigate]);

  const total = cartItems.reduce((s, i) => s + i.price * i.quantity, 0);

  const validate = () => {
    const a = shipping;
    if (!a.fullName.trim()) return 'Please enter recipient name';
    if (!/^\d{10}$/.test(a.phone.trim())) return 'Please enter a valid 10-digit phone number';
    if (!a.addressLine1.trim()) return 'Please enter address line 1';
    if (!a.city.trim()) return 'Please enter city';
    if (!a.state.trim()) return 'Please enter state';
    if (!/^\d{6}$/.test(a.pincode.trim())) return 'Please enter a valid 6-digit pincode';
    return null;
  };

  const handlePlaceOrder = () => {
    const err = validate();
    if (err) { alert(err); return; }
    if (cartItems.length === 0) { alert('Your cart is empty'); return; }

    setPlacingOrder(true);
    setError(null);
    try {
      const shippingStr = formatAddress(shipping);
      const billingStr = useSameAddress ? shippingStr : formatAddress(billing);
      const result = orderService.placeOrder({
        shippingAddress: shippingStr,
        billingAddress: billingStr,
        paymentMethod: 'cod',
        notes: notes.trim(),
        cartItems,
        userId: user!.id,
      });
      cartService.clearCart(user!.id);
      alert(`Order placed! Order #${result.orderNumber}`);
      navigate('/orders');
    } catch {
      setError('Failed to place order. Please try again.');
    } finally {
      setPlacingOrder(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#DAA520]" />
    </div>
  );

  if (cartItems.length === 0) return (
    <div className="flex items-center justify-center py-20">
      <div className="text-center">
        <div className="text-4xl mb-4">🛒</div>
        <h2 className="text-2xl font-bold mb-2">Your cart is empty</h2>
        <button onClick={() => navigate('/')} className="bg-[#DAA520] text-white px-6 py-2 rounded hover:bg-[#c5941c] mt-4">
          Continue Shopping
        </button>
      </div>
    </div>
  );

  return (
    <div className="bg-[#FCFCFC]">
      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-[#1A1A1A] text-white px-6 py-3 rounded-full shadow-lg text-sm font-medium">
          🚀 {toast}
        </div>
      )}

      <div className="py-8 px-4 md:px-20">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-800 mb-8">Checkout</h1>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">{error}</div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left — Forms */}
            <div className="lg:col-span-2 space-y-6">
              <AddressSection title="Shipping Address" form={shipping} setForm={setShipping} />

              {/* Billing address toggle */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold">Billing Address</h2>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={useSameAddress}
                      onChange={e => setUseSameAddress(e.target.checked)}
                      className="rounded border-gray-300 text-[#DAA520] focus:ring-[#DAA520]"
                    />
                    <span className="text-sm text-gray-600">Same as shipping</span>
                  </label>
                </div>
              </div>

              {!useSameAddress && (
                <AddressSection title="Billing Address Details" form={billing} setForm={setBilling} />
              )}

              {/* Payment */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h2 className="text-xl font-semibold mb-5">Payment Method</h2>
                <div className="space-y-3">
                  <div className="flex items-center p-4 border-2 border-[#DAA520] rounded-lg bg-[#DAA520]/5">
                    <input type="radio" checked readOnly className="h-5 w-5 text-[#DAA520]" />
                    <div className="ml-4">
                      <p className="font-medium">Cash on Delivery</p>
                      <p className="text-sm text-gray-500 mt-0.5">Pay when you receive your order</p>
                    </div>
                  </div>
                  <div
                    onClick={() => { setToast('Online payment coming soon!'); setTimeout(() => setToast(null), 3000); }}
                    className="flex items-center p-4 border border-gray-200 rounded-lg cursor-pointer opacity-60 select-none"
                  >
                    <div className="h-5 w-5 rounded-full border-2 border-gray-300 flex-shrink-0" />
                    <div className="ml-4">
                      <p className="font-medium text-gray-500">Online Payment</p>
                      <p className="text-sm text-gray-400 mt-0.5">UPI / Card / Wallet — Coming Soon</p>
                    </div>
                    <span className="ml-auto text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full">Soon</span>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h2 className="text-xl font-semibold mb-4">Order Notes (Optional)</h2>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Any special instructions..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#DAA520] focus:border-transparent text-sm"
                />
              </div>
            </div>

            {/* Right — Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-lg p-6 sticky top-6">
                <h2 className="text-xl font-semibold mb-5">Order Summary</h2>

                <div className="space-y-3 mb-5 max-h-72 overflow-y-auto">
                  {cartItems.map(item => (
                    <div key={item.id} className="flex gap-3 items-center">
                      <img src={encodeURI(item.imageUrl)} alt={item.name} className="w-14 h-14 rounded-lg object-cover flex-shrink-0 bg-gray-100" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{item.name}</p>
                        <p className="text-xs text-gray-500">Qty: {item.quantity} × ₹{item.price.toFixed(0)}</p>
                      </div>
                      <p className="text-sm font-semibold">₹{(item.price * item.quantity).toFixed(0)}</p>
                    </div>
                  ))}
                </div>

                <div className="space-y-2 mb-5 text-sm">
                  <div className="flex justify-between"><span className="text-gray-600">Subtotal</span><span>₹{total.toFixed(0)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Shipping</span><span>₹0</span></div>
                  <div className="border-t border-gray-200 pt-2 flex justify-between text-base font-bold">
                    <span>Total</span><span className="text-[#DAA520]">₹{total.toFixed(0)}</span>
                  </div>
                </div>

                <button
                  onClick={handlePlaceOrder}
                  disabled={placingOrder}
                  className="w-full bg-[#E91E63] hover:bg-[#D81B60] text-white py-3 rounded-lg font-semibold mb-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {placingOrder ? 'Placing Order...' : `Place Order (₹${total.toFixed(0)})`}
                </button>
                <button onClick={() => navigate('/cart')} className="w-full border border-[#DAA520] text-[#DAA520] py-3 rounded-lg font-semibold hover:bg-[#DAA520]/10">
                  Back to Cart
                </button>

                <div className="mt-4 text-xs text-gray-400 space-y-1">
                  <p>✓ Secure checkout</p>
                  <p>✓ 30-day return policy</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
